import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  PlanType,
  SubscriptionStatus,
  FREE_LIMITS,
  TRIAL,
} from '@/config/subscriptionPlans';

// =====================================================
// TYPES
// =====================================================

interface SubscriptionState {
  /** Current plan: free, monthly, annual, family */
  planType: PlanType;
  /** Subscription lifecycle status */
  status: SubscriptionStatus;
  /** Whether user currently has premium access (active, trial, or grace period) */
  isPremium: boolean;
  /** Is the user on a trial right now? */
  isTrialing: boolean;
  /** Has the trial expired without upgrading? */
  isTrialExpired: boolean;
  /** Is this user a grandfathered early adopter? */
  isEarlyAdopter: boolean;
  /** Premium inherited from a parent's subscription (not the user's own) */
  inheritedFromParent: boolean;
  /** Days remaining in trial (0 if not trialing) */
  trialDaysRemaining: number;
  /** ISO string of when trial ends */
  trialEnd: string | null;
  /** ISO string of when current paid period ends */
  currentPeriodEnd: string | null;
  /** Stripe customer ID (for checkout/portal) */
  stripeCustomerId: string | null;
  /** Whether the sub data is still loading */
  loading: boolean;
  /** How many scholarships the user has saved */
  savesCount: number;
  /** Max saves allowed (Infinity for premium) */
  maxSaves: number;
  /** Whether the user can save another scholarship */
  canSave: boolean;
  /** Whether to show the trial expiring banner */
  showTrialBanner: boolean;
}

interface SubscriptionContextType extends SubscriptionState {
  /** Re-fetch subscription data from Supabase */
  refreshSubscription: () => Promise<void>;
}

// =====================================================
// DEFAULTS (logged-out or loading state)
// =====================================================

const defaultState: SubscriptionState = {
  planType: 'free',
  status: 'free',
  isPremium: false,
  isTrialing: false,
  isTrialExpired: false,
  isEarlyAdopter: false,
  inheritedFromParent: false,
  trialDaysRemaining: 0,
  trialEnd: null,
  currentPeriodEnd: null,
  stripeCustomerId: null,
  loading: true,
  savesCount: 0,
  maxSaves: FREE_LIMITS.MAX_SAVES,
  canSave: true,
  showTrialBanner: false,
};

// =====================================================
// CONTEXT
// =====================================================

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [state, setState] = useState<SubscriptionState>(defaultState);

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setState({ ...defaultState, loading: false });
      return;
    }

    // Timeout: if RPC takes longer than 12s, treat user as free
    const timeoutId = setTimeout(() => {
      console.warn('[SubscriptionContext] Fetch timed out — falling back to free tier');
      setState({ ...defaultState, loading: false });
    }, 12000);

    try {
      // Fetch subscription via the helper function
      const { data: subData, error: subError } = await supabase
        .rpc('get_subscription_status', { p_user_id: user.id });

      clearTimeout(timeoutId);

      // Fetch saves count from profiles
      const { data: profileData } = await supabase
        .from('profiles')
        .select('saves_count')
        .eq('user_id', user.id)
        .single();

      const savesCount = profileData?.saves_count ?? 0;

      // Fetch early_adopter flag directly from subscriptions table
      const { data: subRow } = await supabase
        .from('subscriptions')
        .select('early_adopter, current_period_end')
        .eq('user_id', user.id)
        .maybeSingle();

      // Detect early adopter from direct query (column or 2099 date)
      const earlyAdopterFromDB = subRow?.early_adopter === true;
      const periodEndDirect = subRow?.current_period_end || '';
      const earlyAdopterFromDirectDate = periodEndDirect.startsWith('2099');

      if (subError || !subData || subData.length === 0) {
        // No subscription found — treat as free user
        const isEarlyAdopter = earlyAdopterFromDB || earlyAdopterFromDirectDate;
        setState({
          planType: 'free',
          status: 'free',
          isPremium: isEarlyAdopter,
          isTrialing: false,
          isTrialExpired: false,
          isEarlyAdopter,
          inheritedFromParent: false,
          trialDaysRemaining: 0,
          trialEnd: null,
          currentPeriodEnd: null,
          stripeCustomerId: null,
          loading: false,
          savesCount,
          maxSaves: isEarlyAdopter ? Infinity : FREE_LIMITS.MAX_SAVES,
          canSave: isEarlyAdopter ? true : savesCount < FREE_LIMITS.MAX_SAVES,
          showTrialBanner: false,
        });
        return;
      }

      const sub = subData[0] as typeof subData[0] & { inherited_from_parent?: boolean };

      // Check if this subscription is inherited from a parent
      const inherited = sub.inherited_from_parent === true;

      // Fallback #2: current_period_end from RPC data starts with 2099
      const periodEndRPC = sub.current_period_end || '';
      const earlyAdopterFromRPCDate = periodEndRPC.startsWith('2099');

      // Use ANY source to detect early adopter
      const isEarlyAdopter = earlyAdopterFromDB || earlyAdopterFromDirectDate || earlyAdopterFromRPCDate;

      // Early adopters get full premium — override whatever the RPC returns
      if (isEarlyAdopter) {
        setState({
          planType: (sub.plan_type as PlanType) || 'annual',
          status: 'active',
          isPremium: true,
          isTrialing: false,
          isTrialExpired: false,
          isEarlyAdopter: true,
          inheritedFromParent: inherited,
          trialDaysRemaining: 0,
          trialEnd: null,
          currentPeriodEnd: sub.current_period_end,
          stripeCustomerId: sub.stripe_customer_id,
          loading: false,
          savesCount,
          maxSaves: Infinity,
          canSave: true,
          showTrialBanner: false,
        });
        return;
      }

      const isPremium = sub.is_premium;
      const isTrialing = sub.status === 'trial';
      const daysRemaining = sub.days_remaining ?? 0;
      const maxSaves = isPremium ? Infinity : FREE_LIMITS.MAX_SAVES;
      const showTrialBanner = isTrialing && daysRemaining <= TRIAL.BANNER_THRESHOLD_DAYS && daysRemaining > 0;

      // Detect expired trial: status is 'trial' but no days remaining and no premium
      const isTrialExpired = isTrialing && daysRemaining <= 0 && !isPremium;

      setState({
        planType: sub.plan_type as PlanType,
        status: sub.status as SubscriptionStatus,
        isPremium,
        isTrialing: isTrialing && !isTrialExpired,
        isTrialExpired,
        isEarlyAdopter,
        inheritedFromParent: inherited,
        trialDaysRemaining: daysRemaining,
        trialEnd: sub.trial_end,
        currentPeriodEnd: sub.current_period_end,
        stripeCustomerId: sub.stripe_customer_id,
        loading: false,
        savesCount,
        maxSaves,
        canSave: savesCount < maxSaves,
        showTrialBanner,
      });
    } catch (err) {
      clearTimeout(timeoutId);
      console.error('Failed to fetch subscription:', err);
      setState({ ...defaultState, loading: false });
    }
  }, [user]);

  // Fetch on mount and when user changes
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Listen for realtime changes to the subscription row
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('subscription-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Re-fetch when subscription changes (e.g. Stripe webhook updates it)
          fetchSubscription();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchSubscription]);

  // Also listen for pipeline changes (affects saves_count)
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('pipeline-saves-count')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'scholarship_pipeline',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchSubscription();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchSubscription]);

  return (
    <SubscriptionContext.Provider
      value={{
        ...state,
        refreshSubscription: fetchSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

// Safe fallback so consumers don't crash if rendered outside SubscriptionProvider
const SUBSCRIPTION_FALLBACK: SubscriptionContextType = {
  ...defaultState,
  refreshSubscription: async () => {},
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    console.warn('[useSubscription] called outside SubscriptionProvider — returning safe defaults');
    return SUBSCRIPTION_FALLBACK;
  }
  return context;
};
